import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppNav from "@/components/site/AppNav";
import { api, BACKEND_URL, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Clapperboard, Loader2, Sparkles, Palette, Music, Users, Film,
  Play, Wand2, Layers, Check, Circle, Aperture, Volume2,
  Brain, Mic, Waves, Award,
} from "lucide-react";

const AGENT_ICONS = {
  sparkles: Sparkles, palette: Palette, music: Music, users: Users,
  film: Film, clapperboard: Clapperboard,
};

const STAGE_ICONS = {
  planning: Brain,
  storyboard: Aperture,
  scene_1: Film,
  scene_2: Film,
  scene_3: Film,
  narration: Mic,
  mixing: Waves,
  mastering: Award,
};

const EXAMPLES = [
  {
    emoji: "☕", title: "Two Minutes to Brew",
    logline: "A single espresso ties together a barista, a first date, and a job offer all landing in the same 120 seconds.",
    format: "brand_film", genre: "romantic comedy", tone: "playful, warm",
  },
  {
    emoji: "📚", title: "The Last Bookstore",
    logline: "In a near-future city where memory can be uploaded, the last human bookseller becomes the only witness to what people are choosing to forget.",
    format: "short_film", genre: "sci-fi drama", tone: "melancholic, hopeful",
  },
  {
    emoji: "🌘", title: "Signal from Vesta",
    logline: "A shipwrecked terraforming crew discovers the moon they were about to reshape has been trying to talk to them for decades.",
    format: "feature_film", genre: "sci-fi thriller", tone: "ominous, awe-inspiring",
  },
  {
    emoji: "🍫", title: "The Chocolate That Remembers",
    logline: "A 30-second TV ad where every square of chocolate replays the exact moment a person needed it most.",
    format: "tv_ad", genre: "brand, drama", tone: "nostalgic, tender",
  },
];

export default function Director() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [project, setProject] = useState({
    title: "", logline: "", format: "brand_film",
    genre: "", tone: "", audience: "", references: "",
  });
  const [plan, setPlan] = useState(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/director/config");
        setConfig(r.data);
      } catch {}
    })();
  }, [user]);

  useEffect(() => {
    const pid = params.get("project");
    if (pid && user) {
      (async () => {
        try {
          const r = await api.get(`/director/projects/${pid}`);
          setProject({
            title: r.data.title || "", logline: r.data.logline || "",
            format: r.data.format || "brand_film", genre: r.data.genre || "",
            tone: r.data.tone || "", audience: r.data.audience || "",
            references: r.data.references || "",
          });
          setPlan(r.data);
          if (r.data?.status === "running" || r.data?.film_status === "rendering") {
            startPolling(pid);
          }
        } catch {}
      })();
    }
    return () => clearTimeout(pollTimer.current);
    // eslint-disable-next-line
  }, [params, user]);

  function startPolling(pid) {
    clearTimeout(pollTimer.current);
    const tick = async () => {
      try {
        const rr = await api.get(`/director/projects/${pid}`);
        setPlan((prev) => ({ ...(prev || {}), ...rr.data }));
        if (rr.data?.status === "complete" && rr.data?.film_status === "ready") {
          toast.success("Your film is live 🎬");
          return;
        }
        if (rr.data?.status === "failed") {
          toast.error("Production hit a snag. Try Direct again.");
          return;
        }
      } catch {}
      pollTimer.current = setTimeout(tick, 2500);
    };
    pollTimer.current = setTimeout(tick, 2500);
  }

  function loadExample(ex) {
    setProject((p) => ({ ...p, ...ex, audience: "", references: "" }));
  }

  async function direct() {
    if (project.title.trim().length < 3) { toast.error("Give your film a title."); return; }
    if (project.logline.trim().length < 8) { toast.error("Sketch a logline in a sentence."); return; }
    setBusy(true);
    setPlan(null);
    try {
      const r = await api.post("/director/plan", project);
      setPlan(r.data);
      toast.success("Directing your film — the whole thing. Sit back.");
      setTimeout(() => document.getElementById("director-pipeline")?.scrollIntoView({ behavior: "smooth" }), 200);
      if (r.data?.id) startPolling(r.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Directing failed.");
    } finally {
      setBusy(false);
    }
  }

  const running = plan?.status === "running" || plan?.film_status === "rendering";
  const filmReady = plan?.film_status === "ready" && plan?.film_url;

  return (
    <div className="min-h-screen text-[#F4EFE6] relative overflow-hidden" style={{ background: "#04030B" }} data-testid="director-page">
      <CosmicVortexBg active={running} />
      <div className="relative z-10">
        <AppNav />
        <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 md:py-14">
          {!plan ? <BriefIntro /> : <FilmHero plan={plan} project={project} filmReady={filmReady} />}

          {/* Live pipeline */}
          {plan && (
            <section id="director-pipeline" className="my-10 md:my-14 scroll-mt-24">
              <PipelineTicker plan={plan} />
            </section>
          )}

          {/* Film player (once ready) */}
          {filmReady && (
            <section className="mb-14">
              <FilmPlayer plan={plan} />
            </section>
          )}

          {/* The brief */}
          <section className="grid lg:grid-cols-[1.15fr_1fr] gap-6 md:gap-10 mb-14">
            <BriefCard project={project} setProject={setProject} config={config} busy={busy} direct={direct} />
            <ExamplesCard onPick={loadExample} />
          </section>

          {/* Six agents band + outputs */}
          {config?.agents && <AgentsBand agents={config.agents} outputs={plan?.outputs} />}

          {plan?.storyboard && <StoryboardStrip storyboard={plan.storyboard} stages={plan.stages} />}
        </main>
      </div>
    </div>
  );
}

/* -------------------------- HERO -------------------------- */
function BriefIntro() {
  return (
    <header className="mb-10 md:mb-14 text-center max-w-4xl mx-auto">
      <div className="overline text-primary mb-4 flex items-center justify-center gap-2">
        <Clapperboard className="w-4 h-4" /> Director Studio · Draftwave
      </div>
      <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
        You dream it.<br />
        <span className="italic bg-gradient-to-r from-primary via-pink-400 to-orange-300 bg-clip-text text-transparent">
          We direct the whole film.
        </span>
      </h1>
      <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-[#F4EFE6]/70 leading-relaxed">
        Type a title and a logline. Draftwave writes the story, picks the cast, designs the light,
        composes the score, shoots the scenes on Sora 2, records the narration, mixes it, masters it,
        and delivers a real film — all in one press.
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#F4EFE6]/50">
        <span className="px-2 py-1 border border-[#F4EFE6]/20">6 agents</span>
        <span className="px-2 py-1 border border-[#F4EFE6]/20">Sora 2</span>
        <span className="px-2 py-1 border border-[#F4EFE6]/20">TTS-HD Onyx</span>
        <span className="px-2 py-1 border border-[#F4EFE6]/20">ffmpeg master</span>
        <span className="px-2 py-1 border border-primary text-primary">One press</span>
      </div>
    </header>
  );
}

function FilmHero({ plan, project, filmReady }) {
  return (
    <header className="mb-10 md:mb-14">
      <div className="overline text-primary mb-3 flex items-center gap-2">
        <Clapperboard className="w-4 h-4" /> Now directing
      </div>
      <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
        {plan?.title || project.title}
      </h1>
      <p className="mt-3 max-w-3xl text-sm md:text-base text-[#F4EFE6]/70">
        {plan?.logline || project.logline}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
        <span className="px-2 py-1 border border-[#F4EFE6]/20 text-[#F4EFE6]/60">{plan?.format_label}</span>
        {plan?.genre && <span className="px-2 py-1 border border-[#F4EFE6]/20 text-[#F4EFE6]/60">{plan.genre}</span>}
        {plan?.tone && <span className="px-2 py-1 border border-[#F4EFE6]/20 text-[#F4EFE6]/60">{plan.tone}</span>}
        <span className={`px-2 py-1 border ${filmReady ? "border-primary text-primary" : "border-[#F4EFE6]/20 text-[#F4EFE6]/60"}`}>
          {filmReady ? "Film ready" : (plan?.status === "failed" ? "Failed" : "In production")}
        </span>
      </div>
    </header>
  );
}

/* -------------------------- LIVE PIPELINE TICKER -------------------------- */
function PipelineTicker({ plan }) {
  const stages = plan?.stages || [];
  if (stages.length === 0) return null;
  const activeIdx = stages.findIndex((s) => s.status === "running");
  const doneCount = stages.filter((s) => s.status === "done").length;
  const pct = Math.round((doneCount / stages.length) * 100);

  return (
    <div className="border border-[#F4EFE6]/15 bg-[#0A0820]/60 backdrop-blur p-6 md:p-8" data-testid="director-pipeline">
      <div className="flex items-center justify-between mb-5">
        <div className="overline text-primary flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Live production line
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4EFE6]/50">
          {doneCount}/{stages.length} · {pct}%
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {stages.map((s, i) => {
          const Icon = STAGE_ICONS[s.id] || Circle;
          const state = s.status;
          const active = state === "running";
          const done = state === "done";
          const failed = state === "failed";
          return (
            <div
              key={s.id}
              className={
                "relative border p-3 transition duration-500 " +
                (done
                  ? "border-primary/70 bg-primary/10 shadow-[0_0_20px_rgba(255,90,60,0.15)]"
                  : active
                  ? "border-primary bg-primary/15 shadow-[0_0_30px_rgba(255,90,60,0.35)] scale-[1.03]"
                  : failed
                  ? "border-red-500/60 bg-red-500/10"
                  : "border-[#F4EFE6]/10 bg-[#0A0820]/40 opacity-60")
              }
              data-testid={`pipeline-stage-${s.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                {active ? (
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                ) : done ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : failed ? (
                  <Circle className="w-4 h-4 text-red-400" />
                ) : (
                  <Icon className="w-4 h-4 text-[#F4EFE6]/40" />
                )}
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#F4EFE6]/70">{i + 1}</span>
              </div>
              <div className={"font-serif-display text-sm leading-tight " + (done || active ? "text-[#F4EFE6]" : "text-[#F4EFE6]/60")}>
                {s.label}
              </div>
              {s.detail && (
                <div className={"mt-1 text-[10px] leading-snug " + (active ? "text-primary/90" : "text-[#F4EFE6]/40")}>
                  {s.detail}
                </div>
              )}
              {active && <div className="absolute inset-x-0 bottom-0 h-0.5 bg-primary animate-pulse" />}
            </div>
          );
        })}
      </div>
      {/* Big progress bar */}
      <div className="mt-6 h-1 bg-[#F4EFE6]/10 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary via-pink-400 to-orange-300 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* -------------------------- FILM PLAYER -------------------------- */
function FilmPlayer({ plan }) {
  const token = getToken() || "";
  const videoUrl = `${BACKEND_URL}${plan.film_url}${plan.film_url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
  const posterUrl = plan.film_poster_url ? `${BACKEND_URL}${plan.film_poster_url}&token=${encodeURIComponent(token)}` : undefined;
  return (
    <div className="border border-primary bg-[#0A0820]/70 backdrop-blur p-4 md:p-6" data-testid="director-film-ready">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-primary flex items-center gap-2">
          <Play className="w-4 h-4" /> Your film · {plan.film_duration || 24}s cinematic
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4EFE6]/50">
          Sora 2 · TTS-HD · Draftwave master
        </div>
      </div>
      <div className="aspect-video w-full border border-[#F4EFE6]/20 overflow-hidden bg-black">
        <video
          src={videoUrl}
          poster={posterUrl}
          controls
          playsInline
          className="w-full h-full object-cover"
          data-testid="director-film-video"
        />
      </div>
      {plan.film_narration && (
        <div className="mt-4 border-l-2 border-primary/60 pl-3 py-2">
          <div className="overline text-[10px] text-primary mb-1">Narration</div>
          <div className="text-sm text-[#F4EFE6]/85 italic">"{plan.film_narration}"</div>
        </div>
      )}
    </div>
  );
}

/* -------------------------- BRIEF CARD -------------------------- */
function BriefCard({ project, setProject, config, busy, direct }) {
  return (
    <div className="border border-[#F4EFE6]/15 bg-gradient-to-br from-[#0F0A26]/70 to-[#04030B]/60 backdrop-blur p-6 md:p-8 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-10 -right-10 w-56 h-56 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, rgba(255,90,60,0.7), transparent 60%)" }} />
      <div className="overline text-primary mb-3 flex items-center gap-2">
        <Wand2 className="w-4 h-4" /> Two lines. One masterpiece.
      </div>
      <div className="space-y-4 relative">
        <div>
          <label className="overline text-[10px] text-[#F4EFE6]/60 mb-1 block">Working title *</label>
          <input
            type="text"
            value={project.title}
            onChange={(e) => setProject({ ...project, title: e.target.value })}
            placeholder="e.g. Two Minutes to Brew"
            className="w-full px-4 py-3 bg-[#04030B] border border-[#F4EFE6]/20 focus:outline-none focus:border-primary transition text-lg"
            data-testid="director-title-input"
          />
        </div>
        <div>
          <label className="overline text-[10px] text-[#F4EFE6]/60 mb-1 block">One-line story *</label>
          <textarea
            rows={3}
            value={project.logline}
            onChange={(e) => setProject({ ...project, logline: e.target.value })}
            placeholder="A ______ who ______ must ______ before ______."
            className="w-full px-4 py-3 bg-[#04030B] border border-[#F4EFE6]/20 focus:outline-none focus:border-primary transition resize-none"
            data-testid="director-logline-input"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="overline text-[10px] text-[#F4EFE6]/60 mb-1 block">Format</label>
            <select
              value={project.format}
              onChange={(e) => setProject({ ...project, format: e.target.value })}
              className="w-full px-3 py-2.5 bg-[#04030B] border border-[#F4EFE6]/20 focus:outline-none focus:border-primary transition text-sm"
              data-testid="director-format-select"
            >
              {(config?.formats || []).map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline text-[10px] text-[#F4EFE6]/60 mb-1 block">Genre</label>
            <input
              type="text" value={project.genre}
              onChange={(e) => setProject({ ...project, genre: e.target.value })}
              placeholder="sci-fi drama"
              className="w-full px-3 py-2.5 bg-[#04030B] border border-[#F4EFE6]/20 focus:outline-none focus:border-primary transition text-sm"
              data-testid="director-genre-input"
            />
          </div>
          <div>
            <label className="overline text-[10px] text-[#F4EFE6]/60 mb-1 block">Tone</label>
            <input
              type="text" value={project.tone}
              onChange={(e) => setProject({ ...project, tone: e.target.value })}
              placeholder="warm, hopeful"
              className="w-full px-3 py-2.5 bg-[#04030B] border border-[#F4EFE6]/20 focus:outline-none focus:border-primary transition text-sm"
              data-testid="director-tone-input"
            />
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <Button
          onClick={direct}
          disabled={busy}
          className="rounded-none h-16 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-sm shadow-[0_0_40px_rgba(255,90,60,0.35)]"
          data-testid="director-direct-btn"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Wand2 className="w-5 h-5 mr-2" />}
          {busy ? "Directing…" : "Direct the entire film"}
        </Button>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4EFE6]/50">
          Free plan + Sora · takes 5-8 min end-to-end
        </div>
      </div>
    </div>
  );
}

/* -------------------------- EXAMPLES CARD -------------------------- */
function ExamplesCard({ onPick }) {
  return (
    <div>
      <div className="overline text-[#F4EFE6]/50 mb-3">Need a spark? Load an example</div>
      <div className="grid grid-cols-1 gap-2">
        {EXAMPLES.map((ex, i) => (
          <button
            key={i}
            onClick={() => onPick(ex)}
            className="w-full text-left border border-[#F4EFE6]/15 p-3 bg-[#0A0820]/40 hover:border-primary hover:bg-[#0A0820]/80 transition group"
            data-testid={`director-example-${i}`}
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl leading-none">{ex.emoji}</div>
              <div className="min-w-0">
                <div className="font-serif-display text-base leading-tight">{ex.title}</div>
                <div className="mt-0.5 text-[10px] font-mono uppercase tracking-widest text-[#F4EFE6]/40">
                  {ex.format.replace("_", " ")} · {ex.genre}
                </div>
                <p className="mt-1 text-xs text-[#F4EFE6]/60 line-clamp-2">{ex.logline}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- AGENTS BAND -------------------------- */
function AgentsBand({ agents, outputs }) {
  return (
    <section className="mb-14">
      <div className="overline text-[#F4EFE6]/50 mb-4">Your six directors</div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {agents.map((a) => {
          const Icon = AGENT_ICONS[a.icon] || Sparkles;
          const out = outputs?.[a.id];
          const state = out?.status || "idle";
          const active = state === "running";
          const done = state === "done";
          return (
            <div
              key={a.id}
              className={
                "border p-3 backdrop-blur transition " +
                (done ? "border-primary bg-primary/10" :
                 active ? "border-primary/60 bg-primary/5 animate-pulse" :
                 "border-[#F4EFE6]/15 bg-[#0A0820]/60")
              }
              data-testid={`director-agent-${a.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={"w-4 h-4 " + (done || active ? "text-primary" : "text-[#F4EFE6]/60")} />
                {done && <Check className="w-3 h-3 text-primary ml-auto" />}
                {active && <Loader2 className="w-3 h-3 text-primary ml-auto animate-spin" />}
              </div>
              <div className="font-serif-display text-sm leading-tight">{a.label}</div>
              <p className="mt-1 text-[10px] text-[#F4EFE6]/50 leading-snug">{a.tagline}</p>
            </div>
          );
        })}
      </div>
      {outputs && Object.values(outputs).some((o) => o?.status === "done") && (
        <details className="mt-4">
          <summary className="cursor-pointer overline text-xs text-[#F4EFE6]/50 hover:text-primary">
            View the full production package (JSON deliverables from all 6 agents)
          </summary>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Object.entries(outputs).filter(([, o]) => o?.status === "done").map(([id, out]) => (
              <AgentJsonCard key={id} out={out} />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function AgentJsonCard({ out }) {
  const Icon = AGENT_ICONS[out.icon] || Sparkles;
  return (
    <div className="border border-[#F4EFE6]/15 bg-[#0A0820]/70 backdrop-blur p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-primary" />
        <div className="font-serif-display text-lg">{out.label}</div>
      </div>
      {out.payload ? (
        <div className="text-xs text-[#F4EFE6]/80 leading-relaxed space-y-2">
          {Object.entries(out.payload).slice(0, 6).map(([k, v]) => (
            <div key={k}>
              <div className="overline text-[10px] text-primary mb-1">{k.replace(/_/g, " ")}</div>
              <div className="line-clamp-4">
                {typeof v === "string" ? v : JSON.stringify(v).slice(0, 320)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-red-400">{out.error || "no output"}</div>
      )}
    </div>
  );
}

/* -------------------------- STORYBOARD STRIP -------------------------- */
function StoryboardStrip({ storyboard, stages }) {
  const scenes = storyboard?.scenes || [];
  if (scenes.length === 0) return null;
  return (
    <section className="mb-14" data-testid="director-storyboard">
      <div className="overline text-[#F4EFE6]/50 mb-4 flex items-center gap-2">
        <Aperture className="w-4 h-4 text-primary" /> Storyboard · {scenes.length} scenes · {storyboard.duration}s
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {scenes.map((s, i) => {
          const stage = (stages || []).find((x) => x.id === `scene_${i + 1}`);
          const state = stage?.status;
          const active = state === "running";
          const done = state === "done";
          return (
            <div
              key={i}
              className={"border p-4 relative overflow-hidden " + (done ? "border-primary/70" : active ? "border-primary" : "border-[#F4EFE6]/15") + " bg-[#0A0820]/60 backdrop-blur"}
            >
              <div className="absolute top-2 right-2">
                {active && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                {done && <Check className="w-4 h-4 text-primary" />}
              </div>
              <div className="overline text-[10px] text-primary mb-2">Scene {i + 1} · 8s</div>
              <div className="font-serif-display text-lg leading-tight mb-2">{s.caption || "Cinematic beat"}</div>
              <p className="text-xs text-[#F4EFE6]/60 line-clamp-4 leading-relaxed">{s.prompt}</p>
            </div>
          );
        })}
      </div>
      {storyboard.narration && (
        <div className="mt-4 border-l-2 border-primary/60 pl-3 py-2 max-w-3xl">
          <div className="overline text-[10px] text-primary mb-1">Voice-over script</div>
          <div className="text-sm italic text-[#F4EFE6]/80">"{storyboard.narration}"</div>
        </div>
      )}
    </section>
  );
}

/* -------------------------- COSMIC VORTEX BACKGROUND -------------------------- */
function CosmicVortexBg({ active }) {
  const stars = useMemo(() => {
    return Array.from({ length: 220 }).map((_, i) => ({
      x: (i * 127.5) % 1600,
      y: (i * 71.3) % 900,
      r: ((i * 7) % 4) * 0.5 + 0.4,
      d: ((i * 13) % 45) / 10,
      s: 3 + (i % 6),
    }));
  }, []);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden data-testid="director-bg">
      {/* Deep vortex base */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 25% 15%, rgba(255,90,60,0.24) 0%, transparent 55%),\
             radial-gradient(ellipse at 78% 30%, rgba(180,80,255,0.28) 0%, transparent 55%),\
             radial-gradient(ellipse at 50% 100%, rgba(255,200,120,0.14) 0%, transparent 60%),\
             linear-gradient(180deg, #04030B 0%, #0A0820 55%, #04030B 100%)",
        }}
      />
      {/* Aurora sheets */}
      <div className="absolute inset-0 opacity-40 mix-blend-screen"
        style={{
          background: "conic-gradient(from 200deg at 60% 40%, rgba(255,90,60,0.35), rgba(120,80,255,0.35), rgba(80,200,255,0.20), rgba(255,90,60,0.35))",
          filter: "blur(80px)",
          animation: "dwAurora 32s ease-in-out infinite alternate",
        }}
      />
      {/* Star field (deterministic) */}
      <svg className="absolute inset-0 w-full h-full opacity-80" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
        {stars.map((st, i) => (
          <circle
            key={i} cx={st.x} cy={st.y} r={st.r} fill="#F4EFE6"
            opacity={0.35 + (i % 5) * 0.12}
            style={{ animation: `dwStar ${st.s}s ease-in-out ${st.d}s infinite` }}
          />
        ))}
        <style>{`
          @keyframes dwStar { 0%,100% { opacity: 0.15; } 50% { opacity: 1; } }
          @keyframes dwAurora {
            0% { transform: rotate(0deg) scale(1.05); }
            100% { transform: rotate(24deg) scale(1.15); }
          }
        `}</style>
      </svg>
      {/* Comet streaks when active */}
      {active && (
        <>
          <div className="absolute w-40 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-70" style={{ top: "18%", left: "-10%", animation: "dwComet 8s linear infinite" }} />
          <div className="absolute w-52 h-0.5 bg-gradient-to-r from-transparent via-pink-300 to-transparent opacity-60" style={{ top: "62%", left: "-15%", animation: "dwComet 11s linear infinite 4s" }} />
          <style>{`
            @keyframes dwComet {
              0% { transform: translateX(0) translateY(0); opacity: 0; }
              10% { opacity: 0.8; }
              90% { opacity: 0.8; }
              100% { transform: translateX(140vw) translateY(120px); opacity: 0; }
            }
          `}</style>
        </>
      )}
      <div className="absolute inset-0 grain opacity-10" />
    </div>
  );
}
